import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useConfirm } from 'material-ui-confirm';

import { getCourseTemplatesState } from '../../../../../../store/V2/CourseTemplatesSlice';
import { ISectionTemplate } from '../../../../../../store/V2/SectionTemplateSlice';
import {
  getCoursesState,
  TUpdateCourseAsAdmin,
} from '../../../../../../store/V2/CoursesSlice';
import {
  createNewCourseResourceSectionAsAdminForTemplate,
  createNewCourseSectionAsAdminFromTemplate,
  createNewCourseZoomSectionAsAdminFromTemplate,
  getSectionsState,
  ISection,
  LESSON_TYPES,
  SECTION_TYPES,
} from '../../../../../../store/V2/SectionSlice';
import { updateCourseAsAdminSavingSlice } from '../../../../../../store/V2/SavingSlice';
import { useAppSelector } from '../../../../../../hooks';
import SectionTemplateItem from './components/SectionTemplateItem';
import { TCourseTutorType } from '../../../../../../store/V2/CourseTutorsSlice';

interface Step3Props {
  moveToPreviousStep: () => void;
  moveToNextStep: () => void;
}

type TCourseTutorOption = {
  label: string;
  value: string;
  type?: TCourseTutorType;
};

export interface ICustomSectionTemplateItem extends ISectionTemplate {
  originalSectionId?: string;
  loading?: boolean;
  error?: string;
  selectedTutorOption?: TCourseTutorOption | null;
}

export const deleteFromTree = (
  tree: ICustomSectionTemplateItem[],
  id: string
): boolean => {
  let index = 0;

  if (!Array.isArray(tree)) return false;

  // eslint-disable-next-line no-restricted-syntax
  for (const node of tree) {
    if (node._id === id) {
      tree.splice(index, 1);

      return true;
    }

    if (node.sections && node.sections.length > 0) {
      if (deleteFromTree(node.sections, id)) {
        return true;
      }
    }

    index += 1;
  }

  return false;
};

const updateTree = (
  tree: ICustomSectionTemplateItem[],
  id: string,
  newData: Partial<ICustomSectionTemplateItem>
) => {
  let index = 0;

  if (!Array.isArray(tree)) return false;

  // eslint-disable-next-line no-restricted-syntax
  for (const node of tree) {
    if (node._id === id) {
      tree[index] = {
        ...tree[index],
        ...newData,
      };

      return true;
    }

    if (node.sections && node.sections.length > 0) {
      if (updateTree(node.sections, id, newData)) {
        return true;
      }
    }

    index += 1;
  }

  return false;
};

const Step3: React.FC<Step3Props> = ({
  moveToPreviousStep,
  moveToNextStep,
}) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();

  // Store
  const courseTemplatesState = useAppSelector(getCourseTemplatesState);
  const coursesState = useAppSelector(getCoursesState);
  const sectionsState = useAppSelector(getSectionsState);

  // State
  const [sections, setSections] = useState<Array<ICustomSectionTemplateItem>>(
    []
  );
  const [isLoading, setLoading] = useState(false);
  const [initialListFormat, setInitialListFormat] = useState(true);
  const [attemptNavigation, setAttemptNavigation] = useState(false);

  const courseData = coursesState.adminCourse;
  const courseTemplateData = courseTemplatesState.adminCourseTemplate;
  const sectionTemplates = courseTemplateData?.sections || [];
  const courseSections = sectionsState.adminCourseSections || [];

  useEffect(() => {
    if (sectionTemplates.length) {
      setSections(sectionTemplates);
    }
  }, [sectionTemplates]);

  useEffect(() => {
    if (initialListFormat && courseSections && courseSections.length) {
      const loopSubSections = (subSections: ISection[] = []) => {
        subSections.forEach((item) => {
          if (item.template) {
            updateSectionItemAttributes(item.template, {
              originalSectionId: item._id,
            });
          }

          if (item.type === SECTION_TYPES.SECTION) {
            loopSubSections(item.sections);
          }
        });
      };

      courseSections.forEach((item) => {
        if (item.template) {
          updateSectionItemAttributes(item.template, {
            originalSectionId: item._id,
          });
        }

        loopSubSections(item.sections);
      });

      setInitialListFormat(false);
    }
  }, [courseSections]);

  useEffect(() => {
    if (attemptNavigation) {
      attemptToNavigate();
      setAttemptNavigation(false);
    }
  }, [attemptNavigation]);

  const toggleLoading = () => {
    setLoading((prevState) => !prevState);
  };

  const deleteSectionItem = (id: string) => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this item?',
    }).then(() => {
      setSections((prevState) => {
        const newSections = JSON.parse(JSON.stringify([...prevState]));
        deleteFromTree(newSections, id);

        return newSections;
      });
    });
  };

  const updateSectionItemAttributes = (
    id: string,
    attributes: Partial<ICustomSectionTemplateItem>
  ) => {
    setSections((prevState) => {
      const newSections = JSON.parse(JSON.stringify([...prevState]));
      updateTree(newSections, id, attributes);

      return newSections;
    });
  };

  const checkStepCompleted = () => {
    let isStepCompleted = true;

    const _loopSubSections = (
      subSections: ICustomSectionTemplateItem[] = []
    ) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of subSections) {
        if (!item.originalSectionId) {
          isStepCompleted = false;
        }

        if (item.type === SECTION_TYPES.SECTION) {
          _loopSubSections(item.sections);
        }
      }
    };

    // eslint-disable-next-line no-restricted-syntax
    for (const item of sections) {
      if (!item.originalSectionId) {
        isStepCompleted = false;
      }

      _loopSubSections(item.sections);
    }

    return isStepCompleted;
  };

  const attemptToNavigate = async () => {
    toggleLoading();
    const isStepCompleted = checkStepCompleted();

    if (isStepCompleted && courseData) {
      const apiData: TUpdateCourseAsAdmin = {
        _id: courseData._id,
        isStep3Completed: true,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await dispatch(updateCourseAsAdminSavingSlice(apiData));

      if (
        res.payload &&
        res.payload.templateSteps &&
        res.payload.templateSteps.step2 === 'completed'
      ) {
        moveToNextStep();
      }
    }

    toggleLoading();
  };

  const handleSubmit = async () => {
    if (courseData) {
      toggleLoading();

      const loopThroughSubSections = async (
        prevParent: string,
        subSections: ICustomSectionTemplateItem[] = []
      ) => {
        let proceedNext = true;

        let _index = 0;
        // eslint-disable-next-line no-restricted-syntax
        for await (const item of subSections) {
          if (item.originalSectionId) {
            const proceed = await loopThroughSubSections(
              item.originalSectionId,
              item.sections
            );

            if (!proceed) {
              break;
            }
          } else {
            updateSectionItemAttributes(item._id, {
              loading: true,
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let res: any;

            if (item.type === SECTION_TYPES.SECTION) {
              res = await dispatch(
                createNewCourseSectionAsAdminFromTemplate({
                  course: courseData._id,
                  template: item._id,
                  parent: prevParent,
                  sortingKey: _index + 1,
                  title: item.title,
                  description: item.description,
                })
              );
            } else if (
              item.type === SECTION_TYPES.ZOOM &&
              item.dateTime &&
              item.duration &&
              item.selectedTutorOption &&
              item.paymentType
            ) {
              res = await dispatch(
                createNewCourseZoomSectionAsAdminFromTemplate({
                  course: courseData._id,
                  template: item._id,
                  parent: prevParent,
                  sortingKey: _index + 1,
                  title: item.title,
                  dateTime: item.dateTime,
                  duration: item.duration,
                  tutor: item.selectedTutorOption.value,
                  paymentType: item.paymentType,
                  lessonType: LESSON_TYPES.LESSON,
                })
              );
            } else if (
              item.type === SECTION_TYPES.RESOURCE &&
              item.curriculum &&
              item.grade &&
              item.subject &&
              item.subjectCode &&
              item.subjectLevel &&
              item.quiztopiyaTopicId &&
              item.quiztopiyaSubTopicId &&
              item.quiztopiyaTopicName &&
              item.quiztopiyaSubTopicName
            ) {
              res = await dispatch(
                createNewCourseResourceSectionAsAdminForTemplate({
                  course: courseData._id,
                  template: item._id,
                  parent: prevParent,
                  sortingKey: _index + 1,
                  curriculum: item.curriculum,
                  grade: item.grade,
                  subject: item.subject,
                  subjectCode: item.subjectCode,
                  subjectLevel: item.subjectLevel,
                  quiztopiyaTopicId: item.quiztopiyaTopicId,
                  quiztopiyaSubTopicId: item.quiztopiyaSubTopicId,
                  quiztopiyaTopicName: item.quiztopiyaTopicName,
                  quiztopiyaSubTopicName: item.quiztopiyaSubTopicName,
                })
              );
            }

            const parentId =
              res && res.payload && res.payload._id
                ? res.payload._id
                : undefined;

            const attributes: Partial<ICustomSectionTemplateItem> = {
              loading: false,
              error: undefined,
            };

            if (res && res.error) {
              const err = JSON.parse(res.error.message as string);

              if (err.data) {
                if (err.data.error_description) {
                  attributes.error = err.data.error_description;
                }
                if (err.data.message) {
                  attributes.error = err.data.message;
                }
              }

              updateSectionItemAttributes(item._id, attributes);
              proceedNext = false;

              break;
            }

            if (parentId) {
              attributes.originalSectionId = parentId;
            }

            updateSectionItemAttributes(item._id, attributes);

            if (parentId && item.type === SECTION_TYPES.SECTION) {
              const proceed = await loopThroughSubSections(
                parentId,
                item.sections
              );

              if (!proceed) {
                break;
              }
            }
          }
        }

        _index += 1;

        return proceedNext;
      };

      let index = 0;

      // eslint-disable-next-line no-restricted-syntax
      for await (const sectionItem of sections) {
        if (sectionItem.originalSectionId) {
          const proceed = await loopThroughSubSections(
            sectionItem.originalSectionId,
            sectionItem.sections
          );

          if (!proceed) {
            break;
          }
        } else {
          updateSectionItemAttributes(sectionItem._id, {
            loading: true,
          });

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const res: any = await dispatch(
            createNewCourseSectionAsAdminFromTemplate({
              course: courseData._id,
              template: sectionItem._id,
              sortingKey: index + 1,
              title: sectionItem.title,
              description: sectionItem.description,
            })
          );
          const parentId =
            res && res.payload && res.payload._id ? res.payload._id : undefined;

          const attributes: Partial<ICustomSectionTemplateItem> = {
            loading: false,
          };

          if (res && res.error) {
            const err = JSON.parse(res.error.message as string);

            if (err.data) {
              if (err.data.error_description) {
                attributes.error = err.data.error_description;
              }
              if (err.data.message) {
                attributes.error = err.data.message;
              }
            }

            updateSectionItemAttributes(sectionItem._id, attributes);

            break;
          }

          if (parentId) {
            attributes.originalSectionId = parentId;
          }

          updateSectionItemAttributes(sectionItem._id, attributes);

          if (parentId) {
            const proceed = await loopThroughSubSections(
              parentId,
              sectionItem.sections
            );

            if (!proceed) {
              break;
            }
          }
        }
      }

      index += 1;
    }

    toggleLoading();

    setAttemptNavigation(true);
  };

  return (
    <div>
      <Stack gap="10px" minHeight="350px">
        {sections.map((sectionTemplate, index) => {
          const _keyName = `${index + 1}`;
          const _orderNumber = `${index + 1}`;

          return (
            <Stack key={_keyName}>
              <SectionTemplateItem
                keyName={_keyName}
                orderNumber={_orderNumber}
                section={sectionTemplate}
                onDelete={deleteSectionItem}
                updateAttributes={updateSectionItemAttributes}
              />
            </Stack>
          );
        })}
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt="15px"
      >
        <Button variant="text" color="primary" onClick={moveToPreviousStep}>
          Back
        </Button>
        <LoadingButton
          variant="text"
          color="primary"
          onClick={handleSubmit}
          loading={isLoading}
        >
          Next
        </LoadingButton>
      </Stack>
    </div>
  );
};

export default Step3;
